import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {{
 *   values: HTMLElement[],
 *   orderButton: HTMLButtonElement,
 * }} Refs
 */

/**
 * Offers a paid sample of the product's material, shown only while the main product's
 * selected colour is one the samples cover.
 *
 * @extends {Component<Refs>}
 */
export class NaSampleSwatchComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['orderButton'];

    #abortController = new AbortController();

    /** @type {number | null} */
    #resetTimeout = null;

    connectedCallback() {
        super.connectedCallback();

        const { signal } = this.#abortController;
        const section = this.closest('.shopify-section');

        section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

        this.#syncSelection();
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.#abortController.abort();

        if (this.#resetTimeout !== null) {
            clearTimeout(this.#resetTimeout);
            this.#resetTimeout = null;
        }
    }

    /** Called by the Section Rendering API after the block is re-rendered. */
    updatedCallback() {
        this.#syncSelection();
    }

    /**
     * Reads the main product's colour off the variant payload and shows or hides the block.
     *
     * @param {Event} event
     */
    #handleVariantUpdate = (event) => {
        const variant = /** @type {any} */ (event).detail?.resource;
        if (!variant) return;

        const optionKey = this.dataset.mainOptionKey || 'option1';
        const selected = variant[optionKey];
        if (typeof selected !== 'string' || selected === '') return;

        this.hidden = !this.#matches(selected);
        this.#reveal(selected);
    };

    /**
     * @param {string} selected - The main product's selected option value.
     * @returns {boolean}
     */
    #matches(selected) {
        const needle = selected.trim().toLowerCase();
        const showFor = (this.dataset.showForValues || '').trim();

        if (showFor !== '') {
            return showFor
                .split(',')
                .map((value) => value.trim().toLowerCase())
                .filter(Boolean)
                .includes(needle);
        }

        return (this.dataset.availableValues || '')
            .split('|')
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean)
            .includes(needle);
    }

    /** @returns {HTMLElement[]} */
    get #values() {
        const { values } = this.refs;
        return Array.isArray(values) ? values : [];
    }

    /**
     * Reveals the pre-rendered swatch for the main product's selected colour and hides the rest.
     *
     * @param {string} selected
     */
    #reveal(selected) {
        const needle = selected.trim().toLowerCase();

        for (const value of this.#values) {
            value.hidden = (value.dataset.value || '').trim().toLowerCase() !== needle;
        }
    }

    /** Re-reads the markup's own selected state, for the initial pass and after a re-render. */
    #syncSelection() {
        const visible = this.#values.find((value) => !value.hidden);
        if (visible?.dataset.value) this.#reveal(visible.dataset.value);
    }

    /**
     * @returns {Promise<{ item_count: number, items: object[] }>}
     */
    async #fetchCart() {
        const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });

        if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);

        return response.json();
    }

    /** Adds the sample variant to the cart. Called from `on:click`. */
    async addSample() {
        const { orderButton } = this.refs;
        const { variantId, defaultText, addingText, addedText, errorText } = orderButton.dataset;

        if (!variantId || orderButton.disabled) return;

        if (this.#resetTimeout !== null) clearTimeout(this.#resetTimeout);

        orderButton.disabled = true;
        if (addingText) orderButton.textContent = addingText;

        try {
            const response = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: variantId, quantity: 1 }),
            });

            if (!response.ok) throw new Error(`Sample add to cart failed: ${response.status}`);

            await response.json();

            // `cart/add.js` answers with the line item, not the cart, and the cart icon only
            // adds `itemCount` to the badge for the product form's own source — every other
            // source is read as the new total. Send the real cart, or the badge drops to 1.
            const cart = await this.#fetchCart();

            document.dispatchEvent(
                new CartAddEvent(cart, variantId, {
                    source: 'na-sample-swatch',
                    itemCount: cart.item_count,
                    variantId,
                    productId: this.dataset.sampleProductId,
                })
            );

            if (addedText) orderButton.textContent = addedText;
        } catch (error) {
            console.error(error);
            if (errorText) orderButton.textContent = errorText;
        } finally {
            this.#resetTimeout = setTimeout(() => {
                if (defaultText) orderButton.textContent = defaultText;
                orderButton.disabled = false;
                this.#resetTimeout = null;
            }, 2000);
        }
    }
}

if (!customElements.get('na-sample-swatch-component')) {
    customElements.define('na-sample-swatch-component', NaSampleSwatchComponent);
}
